import dayjs from 'dayjs';
import cloneDeep from 'lodash/cloneDeep';

import type { ICrossTab } from '@webapp/account/src/resources/reports/state';

import { parseJSON } from '@webapp/common/lib/utils';
import { LogicTransition, LogicType, QuestionType } from '@webapp/common/resources/survey';

import { mapQuestion } from '../questions/map';

import type { Answer, AnswerReport, AudioInfo } from './answers';
import { initialAnswer, initialAnswerReport } from './answers';
import { mapAnswerReportResp } from './map-answer-report';
import type { Report } from './reports';
import { initialReport } from './reports';

export const mapAnswerReport = (data: {
    ID: number;
    QUESTION: string;
    QUESTION_TYPE_ID: QuestionType;
    FORWARD_PARENT: number | null;
}): AnswerReport => ({
    ...cloneDeep(initialAnswerReport),
    id: data.ID,
    name: data.QUESTION,
    type: data.QUESTION_TYPE_ID,
    question: mapQuestion(data),
});

export const mapAudio = ({
    ID,
    MIME_TYPE,
    PATH,
    SIZE,
}: {
    ID: number;
    SIZE: number;
    MIME_TYPE: string;
    PATH: string;
}): AudioInfo => ({
    id: ID,
    size: SIZE,
    path: PATH,
    mime: MIME_TYPE,
});

const mapLinkParam = ({
    NAME,
    TITLE,
    VALUE,
}: {
    NAME: string;
    TITLE: string;
    VALUE: string;
}): { name: string; value: string; title: string } => ({
    name: NAME,
    title: TITLE,
    value: VALUE,
});

const hasReportAnswer = (report: AnswerReport): boolean =>
    report.rows.length > 0 || report.cols.length > 0 || (report.value !== null && report.value !== undefined);

const getAnsweredPageIds = (reports: Array<AnswerReport>): Set<number> =>
    reports.reduce((acc, report) => {
        if (report.question.type !== QuestionType.TEXT_BLOCK && hasReportAnswer(report)) {
            acc.add(report.question.pageId);
        }
        return acc;
    }, new Set<number>());

const getPagesWithNonTextQuestion = (reports: Array<AnswerReport>): Set<number> =>
    reports.reduce((acc, report) => {
        if (report.question.type !== QuestionType.TEXT_BLOCK) {
            acc.add(report.question.pageId);
        }
        return acc;
    }, new Set<number>());

const safeParseJSON = <T>(value: any, defaultValue: T): T => {
    if (!value || typeof value !== 'string') {
        return defaultValue;
    }
    return parseJSON<T>(value, defaultValue);
};

const getCheckedLogicVariantIds = (logic: any): Array<number> =>
    safeParseJSON<Array<{ id: number | string; checked?: boolean }>>(logic.PARAMS, [])
        .filter((variant) => variant.checked)
        .map((variant) => Number(variant.id))
        .filter((id) => Number.isFinite(id));

const isLogicMatched = (logic: any, selectedAnswerIds: Set<number>): boolean => {
    const type = Number(logic.TYPE);

    if (type === LogicType.ALWAYS) {
        return true;
    }

    if (type === LogicType.SELECT_VARIANTS) {
        return getCheckedLogicVariantIds(logic).some((id) => selectedAnswerIds.has(id));
    }

    return false;
};

const transitionPriority = (transition: number): number => {
    if (transition === LogicTransition.QUESTIONS || transition === LogicTransition.PAGES) {
        return 2;
    }
    if (transition === LogicTransition.ANSWERS_HIDE) {
        return 0;
    }
    return 1;
};

const getVisibleQuestionIds = (dataQuestions: Array<any>, dataAnswers: Array<any>): Set<number> | null => {
    if (!dataQuestions.length) {
        return null;
    }

    // `reorderQuestions` strips the `ORDER` field from ignored question types
    // (e.g. TEXT_BLOCK), so we must rely on the original array order instead of
    // sorting by `ORDER`.
    const orderedQuestions = dataQuestions;

    const selectedAnswersByQuestion = new Map<number, Set<number>>();
    dataAnswers.forEach(({ id, responses }: any) => {
        const selected = new Set<number>();
        responses.forEach((response: any) => {
            if (response.ANSWER_BOOL === 1 && response.ANSWER_ID !== null && response.ANSWER_ID !== undefined) {
                selected.add(Number(response.ANSWER_ID));
            }
        });
        if (selected.size > 0) {
            selectedAnswersByQuestion.set(id, selected);
        }
    });

    const visible = new Set<number>();
    let shownQuestionIds: Set<number> | null = null;
    let shownPageIds: Set<number> | null = null;
    const hiddenQuestionIds = new Set<number>();
    const hiddenPageIds = new Set<number>();
    let jumpToQuestionId: number | null = null;
    let jumpToPageId: number | null = null;
    let finish = false;

    for (const q of orderedQuestions) {
        if (finish) {
            continue;
        }

        const qId = q.ID;
        const pageId = q.PAGE;

        if (jumpToQuestionId !== null && qId !== jumpToQuestionId) {
            continue;
        }
        jumpToQuestionId = null;

        if (jumpToPageId !== null && pageId !== jumpToPageId) {
            continue;
        }
        jumpToPageId = null;

        if (shownPageIds !== null && !shownPageIds.has(pageId)) {
            continue;
        }
        if (hiddenPageIds.has(pageId)) {
            continue;
        }
        if (shownQuestionIds !== null && !shownQuestionIds.has(qId)) {
            continue;
        }
        if (hiddenQuestionIds.has(qId)) {
            continue;
        }

        visible.add(qId);

        const selected = selectedAnswersByQuestion.get(qId);
        if (!selected || !Array.isArray(q.logics)) {
            continue;
        }

        const sortedLogics = [...q.logics].sort(
            (a, b) => transitionPriority(Number(a.GOAL)) - transitionPriority(Number(b.GOAL)),
        );

        for (const logic of sortedLogics) {
            const transition = Number(logic.GOAL);
            const action = logic.TRANSITION_ACTION;

            if (transition === LogicTransition.ANSWERS_HIDE) {
                continue;
            }

            if (!isLogicMatched(logic, selected)) {
                continue;
            }

            if (action === 'HIDE') {
                if (transition === LogicTransition.QUESTIONS) {
                    safeParseJSON<Array<number>>(logic.NEXT_QUESTIONS, []).forEach((id) => hiddenQuestionIds.add(id));
                } else if (transition === LogicTransition.PAGES) {
                    safeParseJSON<Array<number>>(logic.NEXT_PAGES, []).forEach((id) => hiddenPageIds.add(id));
                }
                continue;
            }

            switch (transition) {
                case LogicTransition.PAGE: {
                    const targetPage = logic.NEXT_PAGE ? Number(logic.NEXT_PAGE) : null;
                    if (targetPage !== null && targetPage !== pageId) {
                        jumpToPageId = targetPage;
                    }
                    break;
                }
                case LogicTransition.QUESTION: {
                    const targetQuestion = logic.NEXT_QUESTION ? Number(logic.NEXT_QUESTION) : null;
                    if (targetQuestion !== null && targetQuestion !== qId) {
                        jumpToQuestionId = targetQuestion;
                    }
                    break;
                }
                case LogicTransition.QUESTIONS: {
                    shownQuestionIds = new Set(safeParseJSON<Array<number>>(logic.NEXT_QUESTIONS, []));
                    break;
                }
                case LogicTransition.PAGES: {
                    shownPageIds = new Set(safeParseJSON<Array<number>>(logic.NEXT_PAGES, []));
                    break;
                }
                case LogicTransition.FINISH_SURVEY:
                case LogicTransition.DISQUAL:
                case LogicTransition.PERSONAL_COMPLETE: {
                    finish = true;
                    break;
                }
            }
        }
    }

    return visible;
};

export const mapAnswer = (
    {
        ALIAS,
        ALIAS_NAME,
        audio,
        AUTH_DATA,
        ID,
        IP,
        LINK_PARAMS,
        PASSWORD,
        PROMO_CODE,
        REFERER,
        SERIAL_NUM,
        SOURCE,
        SURVEY_ID,
        TEST,
        TEST_PASSED,
        TEST_PERCENTS,
        TOTAL,
        UPDATED,
        URI,
        AUTH_PHONE,
    }: any,
    dataQuestions: Array<any> = [],
    dataAnswers: Array<any> = [],
): Answer => {
    const reports = dataQuestions.map((q: any) => mapAnswerReportResp(mapAnswerReport(q), dataAnswers, TEST));
    const answeredPageIds = getAnsweredPageIds(reports);
    const pagesWithNonTextQuestion = getPagesWithNonTextQuestion(reports);
    const visibleQuestionIds = getVisibleQuestionIds(dataQuestions, dataAnswers);

    const visibleReports = reports.filter((report) => {
        if (report.question.type !== QuestionType.TEXT_BLOCK) {
            return true;
        }

        const inVisibleQuestionIds = visibleQuestionIds ? visibleQuestionIds.has(report.id) : null;
        const pageHasNonText = pagesWithNonTextQuestion.has(report.question.pageId);
        const pageAnswered = answeredPageIds.has(report.question.pageId);

        return inVisibleQuestionIds !== false && (!pageHasNonText || pageAnswered);
    });

    return {
        ...cloneDeep(initialAnswer),
        id: ID,
        surveyId: SURVEY_ID,
        authData: parseJSON(AUTH_DATA, null),
        lastUpdated: dayjs(UPDATED),
        execTime: TOTAL,
        ipAddress: IP,
        url: URI,
        authPhone: AUTH_PHONE,
        password: PASSWORD,
        promocode: PROMO_CODE,
        alias: ALIAS,
        aliasName: ALIAS_NAME,
        linkParams: Array.isArray(LINK_PARAMS) ? LINK_PARAMS.map(mapLinkParam) : [],
        audio: audio && mapAudio(audio),
        referer: REFERER,
        source: SOURCE,
        testScore: TEST,
        testPassed: TEST_PASSED,
        testPercents: TEST_PERCENTS,
        serialNumber: SERIAL_NUM,
        reports: visibleReports,
    };
};

export const mapReport = (data: {
    ID: number;
    QUESTION: string;
    QUESTION_TYPE_ID: QuestionType;
    ORDER: number;
}): Report => ({
    ...cloneDeep(initialReport),
    id: data.ID,
    name: data.QUESTION,
    type: data.QUESTION_TYPE_ID,
    order: data.ORDER,
    question: mapQuestion(data, true),
});

export const mapCrossTab = (item: {
    ID: number;
    SURVEY_ID: number;
    TITLE: string;
    AFTER_QUESTION: number;
    AFTER_LINK_PARAM: number;
    ORDER: number;
    PARAMS: string;
    questions: Array<{
        ID: number;
        LINK_PARAM_ID: number;
        QUESTION_ID: number;
        ROLE: number;
        TAB_ID: number;
        TYPE: number;
    }>;
}): ICrossTab => ({
    id: item.ID,
    survey_id: item.SURVEY_ID,
    title: item.TITLE,
    after_question: item.AFTER_QUESTION,
    after_link_param: item.AFTER_LINK_PARAM,
    params: item.PARAMS ? JSON.parse(item.PARAMS) : {},
    order: item.ORDER,
    questions: item.questions.map((question) => ({
        id: question.ID,
        linkParamId: question.LINK_PARAM_ID,
        questionId: question.QUESTION_ID,
        role: question.ROLE,
        tabId: question.TAB_ID,
        type: question.TYPE,
    })),
});
